<template>
  <div v-if="showGrid">
    <div class="columns">
      <div class="column" v-if="search">
        <p class="control has-icons-left has-icons-right">
          <input
            class="input"
            type="text"
            id="filter-text-box"
            placeholder="Filtrar "
            v-on:input="onFilterTextBoxChanged()"
          />
          <span class="icon is-medium is-left">
            <i class="material-icons">search</i>
          </span>
        </p>
      </div>
      <div class="column is-one-fifth" v-if="resolvedComponent">
        <component :is="resolvedComponent" :gridApi="gridApi"></component>
      </div>
      <div
        class="column is-one-fifth"
        v-if="menuFilter.active && dataGroupsMenu.length"
      >
        <menu-filter :groups="dataGroupsMenu" :gridApi="gridApi"></menu-filter>
      </div>
      <div class="column is-one-fifth" v-if="uploadFile">
        <upload-file @sendDataFile="sendDataFile"></upload-file>
      </div>
    </div>
    <ag-grid-vue
      style="width: 100%; height: 400px;"
      :class="themeClass"
      :defaultColDef="defaultColDef"
      :columnDefs="columnDefs"
      :rowData="rowData"
      @grid-ready="onGridReady"
      @grid-size-changed="onGridSizeChanged"
      @first-data-rendered="onFirstDataRendered"
      :rowSelection="rowSelection"
      :isRowSelectable="isRowSelectable"
      @selection-changed="onSelectionChanged"
      :pagination="pagination"
      :paginationPageSize="20"
      :includeHiddenColumnsInQuickFilter="true"
      :editType="editType"
      :suppressDragLeaveHidesColumns="true"
      :suppressRowClickSelection="suppressRowClickSelection"
      @cell-value-changed="onCellValueChanged"
      @row-value-changed="onRowValueChanged"
      @cell-clicked="onCellClicked"
      :tooltipShowDelay="tooltipShowDelay"
      :isExternalFilterPresent="isExternalFilterPresent"
      :doesExternalFilterPass="doesExternalFilterPass"
      :pinnedTopRowData="rowPinning"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
/**
 * La documentación del Ag grid es: https://www.ag-grid.com/vue-data-grid/getting-started/
 * Versión: 30.0.0 community - "No es el empresarial, ese es de paga"
 *
 * Comentarios
 *
 * El componente del menuFilter y uploadFile son componentes propios, el cual se crearon para las necesidades de Siete
 *
 * menuFilter...
 *  Crea un menu desplegable donde se listan los diferentes datos de una columna especificada anteriormente
 *  tienen un buscador dentro del componente, este al seleccionar un grupo automáticamente le envía el dato al ag grid
 *  donde se filtra los datos.
 *
 *  estructura de activación:
 *      type: Object
 *      data: {
 *        grupo: String -> Nombre de la columna que desea crearle el filtro *
 *        active: Boolean -> Verdadero o falso para saber si se activa *
 *      }
 *
 * uploadFile...
 *  Crea un menu donde puede cargar un dato, este se enviara automáticamente a una función definida en el componente del ag grid
 *  el cual desde el componente principal se le proporciona la función el cual es "dataUploadFile" y este enviara la data a esa función
 *
 *  estructura de activación:
 *      type: Boolean
 *      data: True o False
 */

import { AgGridVue } from "ag-grid-vue";
import menuFilter from "../frpm/components/menuFilter.vue";
import uploadFile from "../frpm/components/uploadFile.vue";
import TooltipGraphBarHorizontal from "../frpm/components/tooltipGraphBarHorizontal.vue";
import ButtonsActionRelation from "../lideres/components/buttonsActionRelation.vue";
import ButtonsActionEmployee from "../lideres/components/buttonsActionEmployee.vue";
import HeaderDepend from "../lideres/components/headerDependLeader.vue";
import GraphTable from "../cultura/Components/graphTable.vue";
import { mapState } from "vuex";
import Vue from "vue";

export default {
  name: "App",
  data() {
    return {
      // Mostrar o ocultar la tabla completa
      showGrid: true,
      // Variables con las api del Ag grid
      gridApi: null,
      gridColumnApi: null,
      // Variable donde se define la configuración por defecto de todas las columnas
      defaultColDef: {
        // make every column use 'text' filter by default
        sortable: true,
        cellDataType: false,
      },
      editType: null,
      rowSelection: null,
      isRowSelectable: null,
      dataGroupsMenu: [],
      tooltipShowDelay: 0,
      tooltipHideDelay: 2000,
      selectedDataType: null,
    };
  },
  props: {
    // Array con las columnas definidas para la tabla
    columnDefs: Array,
    // Array con los datos que se cargaran en la tabla
    rowData: {
      type: Array,
      default() {
        return [];
      },
    },
    // Boleano el cual define si tiene paginación
    pagination: {
      type: Boolean,
      default() {
        return true;
      },
    },
    // Boleano el cual define si tiene buscador
    search: {
      type: Boolean,
      default() {
        return true;
      },
    },
    // Objeto donde se define la columna y que activa el servicio de menuFilter
    menuFilter: {
      type: Object,
      default() {
        return {
          group: "",
          active: false,
        };
      },
    },
    // Boleano el cual define si se activa el boton de cargar datos
    uploadFile: {
      type: Boolean,
      default() {
        return false;
      },
    },
    // Objeto con el nombre del grupo y su valor
    filters: {
      type: Array,
      default() {
        return null;
      },
    },
    // Funciones para definir filtros externos
    isExternalFilterPresent: {
      type: Function,
      default() {
        return () => false;
      },
    },
    doesExternalFilterPass: {
      type: Function,
      default() {
        return () => true;
      },
    },
    // Boleano el cual define si se activa el seleccionar a las columnas que vengan con un dato llamado empty: true o no
    onDeselectEmptyData: {
      type: Boolean,
      default() {
        return false;
      },
    },
    rowPinning: {
      type: Array,
      default() {
        return [];
      },
    },
    // Boleano el cual define si se activa la columna _actions donde se ingresarian los botones, evitando el selectRow en esa columna
    actions: {
      type: Boolean,
      default() {
        return false;
      },
    },
    // String donde se especifica que tipo de selección van a tener las Rows y el límite de selección en tipo Number
    typeSelection: {
      type: String,
      default() {
        return "single";
      },
    },
    // Boleano el cual define si se deshabilita el seleccionar las filas o no
    suppressRowClickSelection: {
      type: Boolean,
      default() {
        return false;
      },
    },
    // Number el cual define la cantidad maxima a seleccionar en la tabla si tiene el checkbox activo
    limitSelection: {
      type: Number,
      default() {
        return 1;
      },
    },
    // String el cual define el tipo de tema que tendra la tabla
    themeClass: {
      type: String,
      default() {
        return "ag-theme-material";
      },
    },
    // Render Components External
    components: {
      type: Object,
    },
    componentMenu: {
      type: Function,
    },
  },
  components: {
    AgGridVue,
    menuFilter,
    uploadFile,
    tooltipGraphBarHorizontal: TooltipGraphBarHorizontal,
    buttonsActionRelation: ButtonsActionRelation,
    buttonsActionEmployee: ButtonsActionEmployee,
    HeaderDepend,
    graphTable: GraphTable,
  },
  computed: {
    ...mapState(["isOculto", "isMovil"]),
    resolvedComponent() {
      if (this.componentMenu) {
        Vue.component("my-dynamic-component", this.componentMenu());
        return "my-dynamic-component";
      }
      return null;
    },
    resolvedComponents() {
      if (this.components) {
        let componentsRender = 0;
        Object.entries(this.components).forEach((entry) => {
          const [key, value] = entry;
          try {
            Vue.component(key, value());
            componentsRender++;
          } catch (e) {
            console.log(e);
          }
        });
        return componentsRender;
      }
      return null;
    },
  },
  beforeMount: function() {
    // Se asigna la selección especificada al crear el componente a la variable correspondiente
    this.rowSelection = this.typeSelection;
    console.log(this.resolvedComponents);
  },
  created() {
    this.editType = "fullRow";
    if (this.onDeselectEmptyData) {
      this.isRowSelectable = (params) => {
        return !!params.data && !params.data.empty;
      };
    }
  },
  methods: {
    onFirstDataRendered: function(params) {
      params.api.sizeColumnsToFit();
      this.groups();
      if (this.filters && this.filters.length) this.selectOne(this.filters);
    },
    showData(dataType, notEdit) {
      if (dataType) {
        this.selectedDataType = dataType;
        // Actualiza la vista de AG-Grid al cambiar el tipo de datos seleccionado
        this.gridApi.setColumnDefs(this.getColumnDefs(notEdit));
        this.gridApi.sizeColumnsToFit();
      }
    },
    getColumnDefs(notEdit) {
      // Retorna los datos del tipo actualmente seleccionado (data1 o data2 ...)
      return this.columnDefs.map((row) => {
        if (!notEdit.includes(row.field)) {
          return { ...row, field: this.selectedDataType };
        }
        return row;
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    onGridSizeChanged(params) {
      if (!this.isMovil) {
        params.api.sizeColumnsToFit();
      } else {
        params.columnApi.autoSizeAllColumns(false);
      }
    },
    onFilterTextBoxChanged() {
      this.gridApi.setQuickFilter(
        document.getElementById("filter-text-box").value
      );
    },
    onSelectionChanged(params) {
      // console.log('1',params)
      if (
        !["rowClicked", "api"].includes(params.source) &&
        this.validateSelection(params)
      ) {
        const selectedRows = this.gridApi.getSelectedRows();
        this.$emit("selected", selectedRows);
      }
    },
    groups() {
      const group = this.menuFilter.group;
      if (group && this.gridApi) {
        let values = [];
        let data = [];
        this.gridApi.forEachNode((node) => {
          if (node.data[group] !== undefined) {
            const exist = values.includes(node.data[group]);
            if (!exist) values.push(node.data[group]);
            if (!exist)
              data.push({ value: String(node.data[group]), active: false });
          }
        });
        this.dataGroupsMenu = data;
      }
    },
    sendDataFile(data, res) {
      this.$emit("dataUploadFile", data);
    },
    deselectAll() {
      this.gridApi.deselectAll();
    },
    deselectOne(filter) {
      if (Object.keys(filter).length) {
        this.gridApi.forEachNode(function(node) {
          for (const key of filter) {
            if (
              node.data.name === key.name &&
              node.data.subGrupo === key.subGrupo
            ) {
              node.setSelected(false);
            }
          }
        });
      }
    },
    selectOne(filter) {
      const selected = [];
      const deselected = [];
      this.gridApi.forEachNode(function(node) {
        const lengthFilter = Object.keys(filter).length;
        let isRow = [];
        for (const el of filter) {
          if (
            node.data.name === el.name &&
            node.data.subGrupo === el.subGrupo
          ) {
            isRow.push(true);
          }
        }
        if (isRow.length === lengthFilter) {
          selected.push(node);
        } else {
          deselected.push(node);
        }
      });
      this.gridApi.setNodesSelected({ nodes: selected, newValue: true });
      this.gridApi.setNodesSelected({ nodes: deselected, newValue: false });
    },
    onCellValueChanged(event) {
      /**
       * Retorna el nuevo valor de la columna modificada
       *
       * @response event
       *
       * Log
       * console.log('onCellValueChanged: ' + event.colDef.field + ' = ' + event.newValue);
       * */
    },
    onRowValueChanged(event) {
      /**
       * Retorna el nuevo dato de toda la fila modificada
       *
       * @response event
       *
       * Log
       * console.log(event.data);
       * */
      if (event.data) {
        this.$emit("saveNewDataRow", event.data);
      }
    },
    onCellClicked(params) {
      if (this.actions) {
        let editingCells = params.api.getEditingCells();
        const columnEdit = !!editingCells.some((cell) => {
          return cell.rowIndex === params.node.rowIndex;
        });

        if (!columnEdit) {
          if (params.column.colId === "_actions") {
            // cell is from non-select column
            params.node.setSelected(false);
          } else {
            const selectedRows = this.gridApi.getSelectedRows();
            this.$emit("selected", selectedRows[0]);
          }
        } else {
          params.node.setSelected(false);
        }
      }
    },
    validateSelection(params) {
      const selectedRows = params.api.getSelectedNodes();

      let selectedRowsCount = selectedRows.length;

      if (selectedRowsCount > this.limitSelection) {
        while (selectedRowsCount > this.limitSelection) {
          const node = selectedRows[selectedRowsCount - 1];
          node.setSelected(false);
          selectedRowsCount--;
        }
      }

      return selectedRows;
    },
    resetGrid() {
      this.showGrid = false;
      setTimeout(() => {
        this.showGrid = true;
      }, 10);
    },
  },
  watch: {
    typeSelection(nv) {
      this.rowSelection = nv;
    },
  },
};
</script>
<style lang="scss">
@import "~ag-grid-community/styles/ag-grid.css";
@import "~ag-grid-community/styles/ag-theme-material.css";

.ag-theme-material {
  --ag-header-foreground-color: black;
  --ag-font-size: 15px;
  --ag-material-accent-color: #522687;
  --ag-range-selection-border-color: rgba(82, 38, 135, 0.32);
}
</style>



// WEBPACK FOOTER //
// src/components/dashboard/tableAg.vue